import { useState, useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Download, Users, Flag, Gauge, RefreshCw, History, FileText, Trash2, CheckCircle2 } from "lucide-react";
import { factionIcon } from "@/lib/game";
import { api } from "@/lib/api";

export default function EventImportTab() {
  const [lsUrl, setLsUrl] = useState("");
  const [paste, setPaste] = useState("");
  const [showPaste, setShowPaste] = useState(false);
  const [teams, setTeams] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [credits, setCredits] = useState(null);
  const [events, setEvents] = useState([]);
  const [curEvent, setCurEvent] = useState(null); // {url, event_id, event_name}
  const [listJob, setListJob] = useState(null);
  const mountedRef = useRef(true);
  const pollTimer = useRef(null);

  const loadEvents = () => api.events().then(setEvents).catch(() => {});

  const resumeLists = async (eid) => {
    try {
      const full = await api.getEvent(eid);
      setTeams(full.teams || []);
      setCurEvent({ url: full.url || "", event_id: full.event_id || "", event_name: full.event_name || "" });
      setListJob({ running: true, done: full.lists_done || 0, total: full.lists_total || 0 });
      pollLists(eid);
    } catch (e) { console.debug("resume lists failed", e); }
  };

  useEffect(() => {
    mountedRef.current = true;
    api.scraperCredits().then(setCredits).catch(() => {});
    (async () => {
      let evs = [];
      try { evs = await api.events(); } catch { evs = []; }
      setEvents(evs);
      // A list download keeps running on the server even if you leave the tab — resume its progress.
      const running = evs.find((e) => e.lists_running);
      if (running) resumeLists(running.id);
    })();
    return () => { mountedRef.current = false; clearTimeout(pollTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistEvent = async (res, url) => {
    const id = res.event_id || url;
    if (!id) return;
    const meta = { url: url || "", event_id: res.event_id || "", event_name: res.event_name || "" };
    setCurEvent(meta);
    try {
      await api.saveEvent({ id, url: meta.url, event_name: meta.event_name, event_id: meta.event_id, teams: res.teams });
      await loadEvents();
    } catch (e) { console.debug("saveEvent failed", e); }
  };

  const handleResult = (res, url) => {
    if (res.status === "OK" && res.teams.length) {
      setTeams(res.teams);
      setNote("");
      setShowPaste(false);
      persistEvent(res, url);
      toast.success(`Found ${res.teams.length} teams — saved for reuse`);
    } else {
      setTeams(null);
      setNote(res.reason || "No teams found.");
      if (res.status === "BLOCKED") setShowPaste(true);
    }
  };

  const fetchByUrl = async () => {
    if (!lsUrl.trim()) return toast.error("Paste a Longshanks event URL first");
    setBusy(true); setNote("");
    try { handleResult(await api.longshanks({ url: lsUrl.trim() }), lsUrl.trim()); }
    catch { setNote("Fetch failed — try pasting the page source instead."); setShowPaste(true); }
    finally { setBusy(false); }
  };

  const parsePaste = async () => {
    if (!paste.trim()) return toast.error("Paste the event page source first");
    setBusy(true); setNote("");
    try { handleResult(await api.longshanks({ html: paste }), ""); }
    catch { setNote("Could not parse the pasted page."); }
    finally { setBusy(false); }
  };

  const loadSavedEvent = async (ev) => {
    const full = await api.getEvent(ev.id);
    setTeams(full.teams || []);
    setCurEvent({ url: full.url || "", event_id: full.event_id || "", event_name: full.event_name || "" });
    toast.success(`Loaded ${full.event_name || "event"} (${(full.teams || []).length} teams)`);
  };
  const refreshEvent = async (ev) => {
    if (!ev.url && !ev.event_id) return toast.error("No URL stored for this event");
    setBusy(true);
    const url = ev.url || `https://www.longshanks.org/event/${ev.event_id}/`;
    try { handleResult(await api.longshanks({ url }), url); }
    catch { toast.error("Refresh failed"); }
    finally { setBusy(false); }
  };
  const deleteEvent = async (id) => { await api.delEvent(id); setEvents((e) => e.filter((x) => x.id !== id)); };

  const lsPlayerCount = teams ? teams.reduce((n, t) => n + t.players.filter((p) => p.ls_id).length, 0) : 0;
  const playerHasList = (p) => (p.lists || []).some((l) => (l.text || "").trim());
  const teamTotal = (t) => t.players.filter((p) => p.ls_id).length;
  const teamSaved = (t) => t.players.filter((p) => p.ls_id && playerHasList(p)).length;
  const savedCount = teams ? teams.reduce((n, t) => n + teamSaved(t), 0) : 0;
  const remaining = lsPlayerCount - savedCount;

  const pollLists = (eid) => {
    const tick = async () => {
      if (!mountedRef.current) return;
      try {
        const ev = await api.getEvent(eid);
        if (!mountedRef.current) return;
        const total = ev.lists_total || 0;
        const done = ev.lists_done ?? ev.lists_saved ?? 0;
        const stale = ev.lists_updated_at ? (Date.now() - new Date(ev.lists_updated_at).getTime() > 45000) : true;
        if (ev.lists_running && !stale) {
          setListJob({ running: true, done, total });
          pollTimer.current = setTimeout(tick, 2500);
        } else {
          // finished, OR the background job died — show the real saved state either way
          setTeams(ev.teams || []);
          setListJob(null);
          api.scraperCredits().then(setCredits).catch(() => {});
          if (total > 0 && done >= total) toast.success("All army lists downloaded & saved");
          else toast(`Saved ${done}/${total} lists — click “Resume” to fetch the rest`);
        }
      } catch { if (mountedRef.current) pollTimer.current = setTimeout(tick, 3000); }
    };
    pollTimer.current = setTimeout(tick, 2500);
  };

  const downloadLists = async (teamName) => {
    const eid = curEvent?.event_id;
    if (!eid) return toast.error("This event has no Longshanks links — fetch it by URL first");
    const scope = teamName ? teams.find((t) => t.name === teamName) : null;
    const missing = scope ? teamTotal(scope) - teamSaved(scope) : remaining;
    if (missing <= 0) return toast.success("Lists already saved for this selection");
    const label = teamName ? `team “${teamName}”` : "this event";
    if (!window.confirm(`Download ${missing} missing army list${missing === 1 ? "" : "s"} for ${label}? Uses ~${missing} scraper credits once, then saved forever.`)) return;
    try {
      const res = await api.fetchEventLists(eid, teamName);
      if (res.status === "no_players") return toast.error("No Longshanks-linked players found");
      if (res.status === "complete") return toast.success("Lists already saved");
      setListJob({ running: true, done: res.lists_done ?? savedCount, total: res.lists_total ?? lsPlayerCount });
      toast.success(`Downloading ${res.lists_missing} list${res.lists_missing === 1 ? "" : "s"} in the background…`);
      pollLists(eid);
    } catch { toast.error("Could not start list download"); }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-[#121824]/70 border-slate-800 p-4 flex items-start gap-3">
        <Download className="h-5 w-5 text-cyan-400 shrink-0 mt-0.5" />
        <p className="text-sm text-slate-400 flex-1">
          Import a Longshanks event so its registered teams &amp; army lists are available everywhere. Then
          <b className="text-amber-300"> claim your team in “My Team”</b> and
          <b className="text-cyan-300"> pick your opponent in “Matchup Matrix”</b>.
        </p>
      </Card>

      <Card className="bg-[#121824]/70 border-slate-800 p-4 sm:p-5">
        <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
          <h3 className="font-display font-bold uppercase tracking-wide text-sm flex items-center gap-2">
            <Download className="h-4 w-4 text-cyan-400" /> Import an event from Longshanks
          </h3>
          {credits && credits.enabled && credits.remaining != null && (
            <span data-testid="credit-meter" className="text-[11px] font-mono text-slate-400 flex items-center gap-1.5 bg-slate-900/60 border border-slate-700 rounded-full px-2.5 py-1">
              <Gauge className="h-3.5 w-3.5 text-cyan-400" />
              {credits.remaining.toLocaleString()} / {credits.limit.toLocaleString()} scraper credits
            </span>
          )}
          {credits && credits.enabled === false && (
            <span data-testid="credit-meter" className="text-[11px] font-mono text-amber-400/80">one-click disabled — paste mode</span>
          )}
        </div>
        <p className="text-xs text-slate-500 mb-3">Imports are saved automatically so you never spend credits twice.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input data-testid="longshanks-url-input" value={lsUrl} onChange={(e) => setLsUrl(e.target.value)}
            placeholder="https://www.longshanks.org/event/37180/" className="bg-slate-950/60 border-slate-700 flex-1" />
          <Button data-testid="longshanks-fetch-button" onClick={fetchByUrl} disabled={busy}
            className="bg-cyan-500 text-slate-900 hover:bg-cyan-400 font-semibold">
            {busy ? "Fetching…" : "Fetch event"}
          </Button>
        </div>

        {note && <p className="text-xs text-amber-400/90 mt-3 leading-relaxed">{note}</p>}

        <div className="mt-3">
          <button data-testid="toggle-paste-button" onClick={() => setShowPaste((v) => !v)}
            className="text-xs text-cyan-400 hover:text-cyan-300 font-medium">
            {showPaste ? "▾ Hide paste import" : "▸ Longshanks blocking you? Paste the page source instead"}
          </button>
          {showPaste && (
            <div className="mt-2 space-y-2">
              <p className="text-[11px] text-slate-500 leading-relaxed">
                Open the event in your browser, press <b>Ctrl/Cmd+U</b>, select all (<b>Ctrl/Cmd+A</b>), copy, and paste here.
              </p>
              <Textarea data-testid="longshanks-paste-input" value={paste} onChange={(e) => setPaste(e.target.value)}
                placeholder="Paste the full event page HTML here…" className="bg-slate-950/60 border-slate-700 h-28 font-mono text-xs" />
              <Button data-testid="longshanks-parse-button" onClick={parsePaste} disabled={busy}
                className="bg-cyan-500 text-slate-900 hover:bg-cyan-400 font-semibold">Parse pasted page</Button>
            </div>
          )}
        </div>

        {teams && (
          <div className="mt-4" data-testid="longshanks-teams-list">
            {lsPlayerCount > 0 && curEvent?.event_id && (
              <div className="rounded-lg border border-cyan-700/40 bg-cyan-950/20 p-3 mb-3 flex items-center gap-3 flex-wrap">
                <FileText className="h-4 w-4 text-cyan-400 shrink-0" />
                {listJob?.running ? (
                  <div className="flex-1 min-w-[200px]">
                    <p className="text-[12px] text-cyan-200 mb-1">Downloading army lists… {listJob.done}/{listJob.total}</p>
                    <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                      <div className="h-full bg-cyan-400 transition-all" style={{ width: `${listJob.total ? (listJob.done / listJob.total) * 100 : 0}%` }} />
                    </div>
                  </div>
                ) : savedCount >= lsPlayerCount ? (
                  <p className="text-[12px] text-emerald-300 flex-1 flex items-center gap-1.5" data-testid="all-lists-saved">
                    <CheckCircle2 className="h-4 w-4" /> All {lsPlayerCount} army lists saved — no credits needed
                  </p>
                ) : (
                  <>
                    <p className="text-[12px] text-slate-300 flex-1 min-w-[180px]">
                      <span className="text-cyan-300 font-semibold">{savedCount}/{lsPlayerCount}</span> army lists saved. Grab the rest once — stored forever so you never re-spend credits.
                    </p>
                    <Button data-testid="download-all-lists-button" onClick={() => downloadLists()} size="sm"
                      className="bg-cyan-500 text-slate-900 hover:bg-cyan-400 font-semibold shrink-0">
                      <Download className="h-3.5 w-3.5 mr-1.5" />
                      {savedCount > 0 ? `Resume — ${remaining} left` : `Download all ${lsPlayerCount} lists`} (~{remaining} credits)
                    </Button>
                  </>
                )}
              </div>
            )}
            <p className="text-[11px] text-slate-500 uppercase tracking-wider mb-2">{teams.length} teams registered</p>
            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
              {teams.map((t, i) => (
                <div key={`${t.name}-${i}`} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3" data-testid="import-team-row">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-cyan-400 shrink-0" />
                    <p className="text-sm font-semibold text-slate-100 truncate">{t.name}</p>
                    {t.country && <span className="text-[11px] text-slate-500 font-mono flex items-center gap-1"><Flag className="h-3 w-3" />{t.country}</span>}
                    {teamTotal(t) > 0 && (
                      <div className="ml-auto flex items-center gap-2 shrink-0">
                        {teamSaved(t) >= teamTotal(t) ? (
                          <span className="text-[10px] text-emerald-300 flex items-center gap-1" data-testid="team-lists-saved"><CheckCircle2 className="h-3 w-3" /> lists saved</span>
                        ) : (
                          <>
                            <span className="text-[10px] text-slate-500 font-mono">{teamSaved(t)}/{teamTotal(t)} lists</span>
                            {curEvent?.event_id && !listJob?.running && (
                              <Button data-testid="download-team-lists-button" onClick={() => downloadLists(t.name)} size="sm" variant="outline"
                                className="h-6 px-2 text-[10px] border-slate-700 bg-transparent text-cyan-300 hover:bg-slate-800">
                                <Download className="h-3 w-3 mr-1" /> Get
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 pl-6">
                    {t.players.map((p, j) => (
                      <span key={`${p.name}-${j}`} className="text-[11px] text-slate-400 flex items-center gap-1">
                        <img src={factionIcon(p.faction)} alt="" className="h-3.5 w-3.5 object-contain" onError={(e) => (e.currentTarget.style.display = "none")} />
                        {p.name}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {events.length > 0 && (
        <Card className="bg-[#121824]/70 border-slate-800 p-4 sm:p-5" data-testid="saved-events-card">
          <h3 className="font-display font-bold uppercase tracking-wide text-sm mb-1 flex items-center gap-2">
            <History className="h-4 w-4 text-cyan-400" /> Saved Events
          </h3>
          <p className="text-xs text-slate-500 mb-3">Reload instantly with zero scraper credits, or refresh from Longshanks on demand.</p>
          <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
            {events.map((ev) => (
              <div key={ev.id} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 flex items-center gap-3" data-testid="saved-event-row">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-100 truncate">{ev.event_name || `Event ${ev.event_id || ev.id}`}</p>
                  <p className="text-[11px] text-slate-500 font-mono flex items-center gap-1.5 flex-wrap">
                    <span>{ev.team_count ?? 0} teams · {ev.event_id ? `#${ev.event_id}` : "pasted"}</span>
                    {ev.lists_total ? (
                      (ev.lists_saved ?? 0) >= ev.lists_total
                        ? <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> lists saved</span>
                        : <span className="text-amber-400/80">· {ev.lists_saved ?? 0}/{ev.lists_total} lists</span>
                    ) : null}
                  </p>
                </div>
                <Button data-testid="load-event-button" onClick={() => loadSavedEvent(ev)} size="sm"
                  className="bg-cyan-500 text-slate-900 hover:bg-cyan-400 font-semibold shrink-0">Load</Button>
                <Button data-testid="refresh-event-button" onClick={() => refreshEvent(ev)} disabled={busy} size="sm" variant="outline"
                  className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 shrink-0">
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>
                <Button data-testid="delete-event-button" onClick={() => deleteEvent(ev.id)} variant="ghost" size="icon"
                  className="text-slate-500 hover:text-red-400 shrink-0"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
